import { Field, Label, RadioGroup } from '@headlessui/react'
import { useTranslation } from 'react-i18next'
import {
  useLoaderData,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import styled from 'styled-components'

import { Breadcrumb } from '@/components/Breadcrumb.tsx'
import { StyledActionButton } from '@/components/Buttons.tsx'
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@/components/Listbox.tsx'
import { Stepper } from '@/components/Stepper.tsx'
import {
  LocationCard,
  LocationRadio,
} from '@/components/templates/LocationCard.tsx'
import {
  ButtonGroup,
  FullScreenContainer,
  ScrollableContent,
} from '@/components/templates/ScreenLayout.tsx'
import { ScreenWrapper } from '@/components/templates/ScreenWrapper.tsx'
import { Header1, Subheader } from '@/components/Text.tsx'
import { useTrackFormProgress } from '@/hooks/useTrackFormProgress.ts'
import { selectAppointmentTypePath, selectDoctorPath } from '@/router/paths.ts'
import { useAppointmentStore } from '@/store.ts'

import { VelthuisFormStep } from '../../../utils/VelthuisFormSteps.ts'
import { LoaderData, SortBy } from './actions.ts'

const SORT_OPTIONS: SortBy[] = ['alphabetical', 'availability']

const SortField = styled(Field)`
  margin-bottom: 24px;
`

const SelectClinic = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'selectClinic',
  })
  const navigate = useNavigate()
  const { appointmentType, category } = useParams() as {
    category: string
    appointmentType: string
  }
  const { selectedLocation, setSelectedLocation } = useAppointmentStore(
    (state) => ({
      selectedLocation: state.selectedLocation,
      setSelectedLocation: state.setSelectedLocation,
    }),
  )
  const { loadTime, locations, sortBy } = useLoaderData() as LoaderData

  const { trackFormInteraction } = useTrackFormProgress(
    VelthuisFormStep.SelectClinic,
    { loadTime },
  )

  const [, setSearchParams] = useSearchParams()

  const handleSortChange = (value: SortBy) => {
    setSearchParams((prev) => {
      const newSearchParams = new URLSearchParams(prev)
      if (value === 'availability') {
        newSearchParams.set('sortBy', value)
      } else {
        newSearchParams.delete('sortBy')
      }
      return newSearchParams
    })
    trackFormInteraction({
      action: 'click',
      formFieldName: 'sortBy',
      formFieldValue: value,
    })
  }

  return (
    <ScreenWrapper>
      <FullScreenContainer>
        <Breadcrumb
          onClick={() => {
            navigate(
              selectAppointmentTypePath({
                category: category!,
              }),
            )
            trackFormInteraction({
              action: 'previous',
              formFieldName: 'back-to-previous',
            })
          }}
          title={t('breadcrumbLink')}
        />
        <Header1>{t('header')}</Header1>
        <Stepper total={6} step={2} />

        <SortField>
          <Subheader as={Label}>{t('sortLabel')}</Subheader>
          <Listbox value={sortBy} name="sortBy" onChange={handleSortChange}>
            <ListboxButton>{t(`sortOptions.${sortBy}`)}</ListboxButton>
            <ListboxOptions>
              {SORT_OPTIONS.map((option) => (
                <ListboxOption key={option} value={option}>
                  {t(`sortOptions.${option}`)}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </Listbox>
        </SortField>
        <ScrollableContent>
          <RadioGroup
            name="selectedLocation"
            id="selectedLocation"
            // Loader refetches (sort changes) create fresh Location objects;
            // compare by key so the stored selection stays checked.
            by="emmaId"
            value={selectedLocation}
            onChange={(value) => {
              setSelectedLocation(value)
              trackFormInteraction({
                action: 'click',
                formFieldName: 'location',
                formFieldValue: value.name,
                formFieldValues: locations?.map(
                  (location) => location.name || '',
                ),
              })
            }}
          >
            {locations?.map((location) => (
              <LocationRadio
                value={location}
                key={location.emmaId}
                aria-label={location.name || location.fullName}
              >
                <LocationCard location={location} />
              </LocationRadio>
            ))}
          </RadioGroup>
        </ScrollableContent>
        <ButtonGroup>
          <StyledActionButton
            variant="Primary"
            type="submit"
            disabled={!selectedLocation}
            onClick={() => {
              if (!selectedLocation) return
              navigate(
                selectDoctorPath({
                  appointmentType: appointmentType,
                  category: category,
                  locationId: selectedLocation.emmaId,
                }),
              )
              trackFormInteraction({
                action: 'next',
                formFieldName: 'submit',
              })
            }}
          >
            {t('selectDoctor')}
          </StyledActionButton>
        </ButtonGroup>
      </FullScreenContainer>
    </ScreenWrapper>
  )
}

export { SelectClinic }
